"use client"

import type React from "react"
import { useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { MessageCircle, Plus, Filter, Star } from "lucide-react"
import { User, Lock, EyeOff, Eye, CheckCircle, XCircle, Mail } from "lucide-react"
import { testimonials, ministries } from "../data/mockData"
import type { Testimonial } from "../types"
import TestimonialModal from "../components/TestimonialModal"
import LoginPromptModal from "../components/LoginPromptModal"
import { getAllUsers, checkLoginStatus } from "../api/authService"
import { getAllComments } from "../api/adminService"

const TestimonialsPage_v1: React.FC = () => {
  const [filteredTestimonials, setFilteredTestimonials] = useState<Testimonial[]>(testimonials)
  const [selectedMinistry, setSelectedMinistry] = useState<string>("all")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [likedTestimonials, setLikedTestimonials] = useState<Set<string>>(new Set())

  const [allUsers, setAllUsers] = useState([])
  const [comments, setComments] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      const users = await getAllUsers();
      if (users) {
        setAllUsers(users);
      }
    };
    const fetchComments = async () => {
      const all: any = await getAllComments();
      if (all) setComments(all);
    };

    fetchUsers();
    fetchComments();
  }, [isModalOpen]);

  const transformedComments = comments.map((c: any) => {
    const pageMatch = c.link.match(/\.com\/([^#]+)/);
    const page = pageMatch && pageMatch[1] ? decodeURIComponent(pageMatch[1]) : "Unknown";
    return {
      id: c.id,
      author: c.author_name || "Anonymous",
      avatar: c.author_avatar_urls?.[48] || "/default-avatar.png",
      email: "", // Email is not available in public comment JSON
      content: c.content?.rendered?.replace(/<[^>]+>/g, "") ?? "",
      ministry: page,
      title: c.meta.testimonial_title,
      likes: 0,
      status: c.status,
      createdAt: new Date(c.date),
      isPinned: false,
      replies: 0, // Adjust if you plan to load reply count later
      original: c,
    };
  });

  // const filteredComments = useMemo(() => {
  //   return transformedComments.filter((c: any) => c.ministry !== "2025/02/10/hello-world/");
  // }, [transformedComments]); ////Check the array for pinned testimonials after it is implemented

  const filteredComments = useMemo(() => {
    const ministries = transformedComments.filter((c: any) => c.ministry !== "2025/02/10/hello-world/");
    return selectedMinistry === "all"
      ? ministries
      : ministries.filter((c: any) => c.ministry === selectedMinistry);
  }, [selectedMinistry, transformedComments]);

  const handleFilterChange = (ministryId: string) => {
    setSelectedMinistry(ministryId)
    if (ministryId === "all") {
      setFilteredTestimonials(testimonials)
    } else {
      setFilteredTestimonials(testimonials.filter((t) => t.ministryId === ministryId))
    }
  }

  const handleLike = (testimonialId: string) => {
    setLikedTestimonials((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(testimonialId)) {
        newSet.delete(testimonialId)
      } else {
        newSet.add(testimonialId)
      }
      return newSet
    })
  }

  const sortedTestimonials = [...filteredComments].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1
    if (!a.isPinned && b.isPinned) return 1
    return b.likes - a.likes
  })

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 pt-20">
      <header className="backdrop-blur-md border-b border-gray-700/50 sticky top-28 pt-5 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Star className="h-8 w-8 text-amber-400" />
                <h1 className="text-2xl font-bold text-gray-100">Community Testimonials</h1>
              </div>
            </div>
            <motion.button
              onClick={() => setIsModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white px-6 py-3 rounded-full font-medium flex items-center space-x-2 transition-all duration-300 shadow-lg shadow-amber-900/30"
            >
              <Plus className="h-5 w-5" />
              <span>Share Your Testimony</span>
            </motion.button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center mb-6">
            <Filter className="h-6 w-6 text-amber-400 mr-2" />
            <h3 className="text-xl font-semibold text-gray-100">Filter by Ministry</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedMinistry("all")}
              className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${selectedMinistry === "all"
                  ? "bg-gradient-to-r from-amber-600 to-amber-500 text-white shadow-lg shadow-amber-900/30"
                  : "bg-gray-800/60 text-gray-300 hover:bg-gray-700/60 border border-gray-600/50"
                }`}
            >
              All Testimonials
            </button>
            {ministries.map((ministry) => (
              <button
                key={ministry.id}
                onClick={() => setSelectedMinistry(ministry.url)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${selectedMinistry === ministry.url
                    ? "bg-gradient-to-r from-purple-600 to-orange-600 text-white shadow-lg shadow-purple-900/30"
                    : "bg-gray-800/60 text-gray-300 hover:bg-gray-700/60 border border-gray-600/50"
                  }`}
              >
                {ministry.name}
              </button>
            ))}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence>
              {sortedTestimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl p-6 shadow-lg shadow-gray-950/50 hover:shadow-xl hover:shadow-amber-900/20 transition-all duration-300 hover:-translate-y-1 relative border border-gray-700/50"
                >
                  {testimonial.isPinned && (
                    <div className="absolute top-4 right-4">
                      <div className="bg-amber-600/20 text-amber-300 px-2 py-1 rounded-full text-xs font-medium flex items-center border border-amber-500/30">
                        <Star className="h-3 w-3 mr-1" fill="currentColor" />
                        Pinned
                      </div>
                    </div>
                  )}

                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.avatar || "/default-avatar.png"}
                      alt={testimonial.author}
                      className="h-12 w-12 rounded-full object-cover border-2 border-amber-400/50"
                    />
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-100">{testimonial.author}</h4>
                      <p className="text-orange-400 text-sm font-medium">
                        {testimonial.ministry
                        .replace(/\/|-/g, " ")
                        .replace(/\b\w/g, (char) => char.toUpperCase())}
                        </p>
                    </div>
                  </div>
                  


                  <p className="text-gray-300 leading-relaxed mb-1 line-clamp-6 font-extrabold">{testimonial.title}</p>


                  <p className="text-gray-300 leading-relaxed mb-6 line-clamp-6 text-sm">
                    <div
                    className="text-gray-300"
                    dangerouslySetInnerHTML={{ __html: testimonial.content }}
                  />
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-700/50">
                    {/* <button
                      onClick={() => handleLike(testimonial.id)}
                      className={`flex items-center space-x-2 transition-colors ${likedTestimonials.has(testimonial.id) ? "text-rose-400" : "text-gray-400 hover:text-rose-400"
                        }`}
                    >
                      <Heart
                        className="h-5 w-5"
                        fill={likedTestimonials.has(testimonial.id) ? "currentColor" : "none"}
                      />
                      <span className="font-medium">
                        {testimonial.likes + (likedTestimonials.has(testimonial.id) ? 1 : 0)}
                      </span>
                    </button> */}

                    <span className="text-gray-500 text-sm">{testimonial.createdAt.toLocaleDateString()}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {filteredComments.length === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
              <MessageCircle className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-400 mb-2">No testimonials found</h3>
              <p className="text-gray-500">Be the first to share a testimony for this ministry!</p>
            </motion.div>
          )}
        </motion.section>
      </main>

      <TestimonialModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  )
}


const TestimonialsPage: React.FC = () => {
  const [filteredTestimonials, setFilteredTestimonials] = useState<Testimonial[]>(testimonials)
  const [selectedMinistry, setSelectedMinistry] = useState<string>("all")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [likedTestimonials, setLikedTestimonials] = useState<Set<string>>(new Set())
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false)

  const [allUsers, setAllUsers] = useState([])
  const [comments, setComments] = useState([])

  useEffect(() => {
    const fetchUsers = async () => {
      const users = await getAllUsers()
      if (users) {
        setAllUsers(users)
      }
    }
    const fetchComments = async () => {
      const all: any = await getAllComments()
      if (all) setComments(all)
    }

    fetchUsers()
    fetchComments()
  }, [isModalOpen])

  useEffect(() => {
    const verifyLoginStatus = async () => {
      const loggedIn = await checkLoginStatus()
      setIsUserLoggedIn(loggedIn)
    }
    verifyLoginStatus()
  }, [])

  const transformedComments = comments.map((c: any) => {
    const pageMatch = c.link.match(/\.com\/([^#]+)/)
    const page = pageMatch && pageMatch[1] ? decodeURIComponent(pageMatch[1]) : "Unknown"
    return {
      id: c.id,
      author: c.author_name || "Anonymous",
      avatar: c.author_avatar_urls?.[48] || "/default-avatar.png",
      email: "", // Email is not available in public comment JSON
      content: c.content?.rendered?.replace(/<[^>]+>/g, "") ?? "",
      ministry: page,
      title: c.meta.testimonial_title,
      likes: 0,
      status: c.status,
      createdAt: new Date(c.date),
      isPinned: false,
      replies: 0, // Adjust if you plan to load reply count later
      original: c,
    }
  })

  const filteredComments = useMemo(() => {
    const ministries = transformedComments.filter((c: any) => c.ministry !== "2025/02/10/hello-world/")
    return selectedMinistry === "all" ? ministries : ministries.filter((c: any) => c.ministry === selectedMinistry)
  }, [selectedMinistry, transformedComments])

  const handleFilterChange = (ministryId: string) => {
    setSelectedMinistry(ministryId)
    if (ministryId === "all") {
      setFilteredTestimonials(testimonials)
    } else {
      setFilteredTestimonials(testimonials.filter((t) => t.ministryId === ministryId))
    }
  }

  const handleLike = (testimonialId: string) => {
    setLikedTestimonials((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(testimonialId)) {
        newSet.delete(testimonialId)
      } else {
        newSet.add(testimonialId)
      }
      return newSet
    })
  }

  const handleShareTestimonyClick = async () => {
    const loggedIn = await checkLoginStatus()
    if (!loggedIn) {
      setShowLoginPrompt(true)
    } else {
      setIsModalOpen(true)
    }
  }

  const handleLoginSuccess = () => {
    setShowLoginPrompt(false)
    window.location.reload() // Refresh to update user state
  }

  const sortedTestimonials = [...filteredComments].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1
    if (!a.isPinned && b.isPinned) return 1
    return b.likes - a.likes
  })

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-slate-900 pt-20">
      <header className="backdrop-blur-md border-b border-gray-700/50 sticky top-28 pt-5 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Star className="h-8 w-8 text-amber-400" />
                <h1 className="text-2xl font-bold text-gray-100">Community Testimonials</h1>
              </div>
            </div>
            <motion.button
              onClick={handleShareTestimonyClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white px-6 py-3 rounded-full font-medium flex items-center space-x-2 transition-all duration-300 shadow-lg shadow-amber-900/30"
            >
              <Plus className="h-5 w-5" />
              <span>Share Your Testimony</span>
            </motion.button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center mb-6">
            <Filter className="h-6 w-6 text-amber-400 mr-2" />
            <h3 className="text-xl font-semibold text-gray-100">Filter by Ministry</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedMinistry("all")}
              className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                selectedMinistry === "all"
                  ? "bg-gradient-to-r from-amber-600 to-amber-500 text-white shadow-lg shadow-amber-900/30"
                  : "bg-gray-800/60 text-gray-300 hover:bg-gray-700/60 border border-gray-600/50"
              }`}
            >
              All Testimonials
            </button>
            {ministries.map((ministry) => (
              <button
                key={ministry.id}
                onClick={() => setSelectedMinistry(ministry.url)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                  selectedMinistry === ministry.url
                    ? "bg-gradient-to-r from-purple-600 to-orange-600 text-white shadow-lg shadow-purple-900/30"
                    : "bg-gray-800/60 text-gray-300 hover:bg-gray-700/60 border border-gray-600/50"
                }`}
              >
                {ministry.name}
              </button>
            ))}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence>
              {sortedTestimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl p-6 shadow-lg shadow-gray-950/50 hover:shadow-xl hover:shadow-amber-900/20 transition-all duration-300 hover:-translate-y-1 relative border border-gray-700/50"
                >
                  {testimonial.isPinned && (
                    <div className="absolute top-4 right-4">
                      <div className="bg-amber-600/20 text-amber-300 px-2 py-1 rounded-full text-xs font-medium flex items-center border border-amber-500/30">
                        <Star className="h-3 w-3 mr-1" fill="currentColor" />
                        Pinned
                      </div>
                    </div>
                  )}

                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.avatar || "/default-avatar.png"}
                      alt={testimonial.author}
                      className="h-12 w-12 rounded-full object-cover border-2 border-amber-400/50"
                    />
                    <div className="ml-4">
                      <h4 className="font-semibold text-gray-100">{testimonial.author}</h4>
                      <p className="text-orange-400 text-sm font-medium">
                        {testimonial.ministry.replace(/\/|-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-300 leading-relaxed mb-1 line-clamp-6 font-extrabold">{testimonial.title}</p>

                  <p className="text-gray-300 leading-relaxed mb-6 line-clamp-6 text-sm">
                    <div className="text-gray-300" dangerouslySetInnerHTML={{ __html: testimonial.content }} />
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-700/50">
                    {/* <button
                      onClick={() => handleLike(testimonial.id)}
                      className={`flex items-center space-x-2 transition-colors ${likedTestimonials.has(testimonial.id) ? "text-rose-400" : "text-gray-400 hover:text-rose-400"
                        }`}
                    >
                      <Heart
                        className="h-5 w-5"
                        fill={likedTestimonials.has(testimonial.id) ? "currentColor" : "none"}
                      />
                      <span className="font-medium">
                        {testimonial.likes + (likedTestimonials.has(testimonial.id) ? 1 : 0)}
                      </span>
                    </button> */}

                    <span className="text-gray-500 text-sm">{testimonial.createdAt.toLocaleDateString()}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {filteredComments.length === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
              <MessageCircle className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-400 mb-2">No testimonials found</h3>
              <p className="text-gray-500">Be the first to share a testimony for this ministry!</p>
            </motion.div>
          )}
        </motion.section>
      </main>

      <TestimonialModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  )
}


export default TestimonialsPage
